import { useLazyQuery } from "@apollo/client";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import DoneIcon from "@mui/icons-material/Done";
import SearchIcon from "@mui/icons-material/Search";
import TagIcon from "@mui/icons-material/Tag";
import {
  Badge,
  Box,
  Button,
  Divider,
  Fab,
  FormControl,
  Grid,
  InputAdornment,
  Stack,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import { fetchAllMultislotContentList, fetchTagList } from "@platformx/authoring-apis";
import { searchIcon } from "@platformx/shared/static-assets";
import { BackArrow, Loader, ShowToastError, capitalizeFirstLetter } from "@platformx/utilities";
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import GalleryItems from "../ContentGallery/GalleryItems";
import useDyanamicGalleryStyle from "./DynamicContentGallery.styles";
import GalleryItemSearchResults from "./GalleryItemSearchResult";
import GalleryLeftSideBar from "./GalleryLeftSideBar";
import { ContentProps } from "./utils/contentGalleryTypes";

type DynamicContentGalleryProps = {
  prelemId?: any;
  multi_select?: boolean;
  handleSelectedContent?: any;
  onToggleContentGallery?: any;
  single_select?: any;
  selectedItems?: any;
  name?: string;
  allowed_content?: any;
  allowed_collection?: any;
  pageFrom?: string;
  selected_filter?: any;
  allowEmptySelection?: boolean;
  // if true, allowed_content has 5 types, but only 3 of them available then stripe remaining 2.
  restrictToAvailableCDPTags?: boolean;
};

/**
 *
 * @param arr array
 * @param multi_select boolean
 * @returns array
 */
const customPairAdded = (arr, multi_select, single_select) => {
  if (multi_select || single_select) {
    const newArray = arr.map((ele) => {
      return {
        ...ele,
        is_checked: false,
      };
    });
    return newArray;
  }
  return arr;
};

const DynamicContentGallery = (props: DynamicContentGalleryProps) => {
  const {
    prelemId,
    multi_select = false,
    handleSelectedContent,
    onToggleContentGallery,
    single_select = false,
    selectedItems = [],
    name,
    allowed_content,
    allowed_collection,
    selected_filter,
    allowEmptySelection = false,
    restrictToAvailableCDPTags = false,
  } = props;
  const filter = "ALL";
  const rows = 40;

  const { t } = useTranslation();
  const classes = useDyanamicGalleryStyle();
  const theme = useTheme();

  const [startIndex, setStartIndex] = useState<number>(0);
  const [isLazyLoad, setIsLazyLoad] = useState<boolean>(false);
  const [isHideSearchBox, setHideSearchBox] = useState<boolean>(false);
  const [isShowItems, setShowItems] = useState<boolean>(true);
  const [isShowCategory, setShowCategory] = useState<boolean>(false);
  const [tagData, setTagData] = useState<any>({});
  const [items, setItems] = useState<ContentProps[]>([]);
  const [searchItems, setSearchItems] = useState<ContentProps[]>([]);
  const [isSuggestionEnabled, setSuggestionEnabled] = useState<boolean>(false);
  const [loading, setLoading] = useState(true);
  const selectedItemsMultiSelect = useRef<any>([...selectedItems]);

  //New Additions - Added to handle selected_filter
  const {
    search: searchTerm = "",
    selected_cdp_tags: selectedCDPTagsFromContent = [],
    selected_tags: selectedTagsFromContent = [],
  } = selected_filter || {};
  const [search, setSearch] = useState(searchTerm || "");
  const [allowedCDPTags, setAllowedCDPTags] = useState<any>([]);
  const [selectedCDPTags, setSelectedCDPTags] = useState<any>(selectedCDPTagsFromContent || []);
  const selectedCDPTagsRef = useRef<any>(selectedCDPTagsFromContent || []);
  const [selectedTags, setSelectedTags] = useState<any>(selectedTagsFromContent || []);
  const selectedTagsRef = useRef<any>(selectedTagsFromContent || []);
  //End New Additions

  const [fetchMultiSlotContentList] = useLazyQuery(fetchAllMultislotContentList);
  const [runFetchTagList] = useLazyQuery(fetchTagList);

  function debounce(func, timeout = 500) {
    let timer;
    return (...args) => {
      clearTimeout(timer);
      timer = setTimeout(() => {
        func(...args);
      }, timeout);
    };
  }
  const getTagData = () => {
    runFetchTagList({
      variables: { start: 0, rows: 1000 },
    })
      .then((res) => {
        if (res?.data?.authoring_getTagsList) {
          setTagData(res?.data?.authoring_getTagsList);
        }
      })
      .catch((err) => {
        console.error(JSON.stringify(err, null, 2));
      });
    // }
  };

  const backToContentHandel = () => {
    setHideSearchBox(false);
  };
  const mobileSearchIconHandel = () => {
    setHideSearchBox(true);
  };

  //List of tags that are allowed for this prelem
  const getInitialContentAndCollectionTags = () => {
    const allowedData = [
      ...(Array.isArray(allowed_content) && allowed_content.length > 0 ? allowed_content : []),
      ...(Array.isArray(allowed_collection) && allowed_collection.length > 0
        ? allowed_collection
        : []),
    ];
    return allowedData?.length > 0 ? allowedData : [];
  };

  // Subset of getInitialContentAndCollectionTags() limited to types that actually exist in the
  // tag taxonomy (Content Type / Collection categories) AND are allowed. These are exactly the
  // tags GalleryLeftSideBar renders as chips, so restricting the default selection to this set
  // keeps the saved filter aligned with what the user can see and toggle.
  const getAvailableContentAndCollectionTags = () => {
    if (!Array.isArray(tagData)) return [];
    const findTags = (categoryName: string) =>
      tagData.find((c) => c?.category?.toLowerCase() === categoryName)?.tags || [];

    const availableContent =
      Array.isArray(allowed_content) && allowed_content.length > 0
        ? findTags("content type").filter((tag) => allowed_content.includes(tag))
        : [];
    const availableCollection =
      Array.isArray(allowed_collection) && allowed_collection.length > 0
        ? findTags("collection").filter((tag) => allowed_collection.includes(tag))
        : [];
    return [...availableContent, ...availableCollection];
  };

  const getQuery = () => {
    let cdpText = "";
    let tagText = "";
    let searchText = "";
    if (selectedCDPTags.length > 0) {
      cdpText = selectedCDPTags.join(", ");
    }
    if (selectedTags.length > 0) {
      tagText = selectedTags.join(", ");
    }
    if (search) {
      searchText = `"${search}"`;
    }
    return (
      <span>
        {cdpText ? (
          <span>
            <span className={classes.activeColor}>FOR </span>
            <span>&quot;{cdpText}&quot;</span>
          </span>
        ) : null}
        {tagText ? (
          <span>
            <span className={classes.activeColor}> AND </span>
            <span>&quot;{tagText}&quot;</span>
          </span>
        ) : null}
        {searchText ? (
          <span>
            <span className={classes.activeColor}> WITH SEARCH TERM </span>
            <span>&quot;{searchText}&quot;</span>
          </span>
        ) : null}
        .
      </span>
    );
  };

  /**
   *save handle
   */
  const handleDone = () => {
    const selectedFilter = {
      searchTerm: search,
      selectedCDPFilter: selectedCDPTagsRef.current,
      selectedTags: selectedTagsRef.current,
    };
    if (multi_select || single_select) {
      let filterItem: any = [];
      filterItem = items.filter((ele: any) => ele?.is_checked);
      if (filterItem.length > 0) {
        // Maintain the order of selection - Important to retain the order of selection.
        const orderedFilteredItem = [...filterItem].sort((a: any, b: any) => {
          const aIndex = selectedItemsMultiSelect.current.findIndex(
            (item: any) => item?.CurrentPageURL === a?.CurrentPageURL,
          );
          const bIndex = selectedItemsMultiSelect.current.findIndex(
            (item: any) => item?.CurrentPageURL === b?.CurrentPageURL,
          );
          return aIndex - bIndex;
        });
        handleSelectedContent({ slots: orderedFilteredItem, selectedFilter }, name);
      } else if (allowEmptySelection) {
        // No items selected - fall back to query behavior (only for hybrid query flows)
        const queryParam = {
          filter: filter,
          cdpFilter: selectedCDPTagsRef.current,
          tags: selectedTagsRef.current,
          searchTerm: search || "",
          pagination: {
            start: 0,
            rows: prelemId === "Prelem_053" ? 5 : 4,
          },
        };
        handleSelectedContent({ slots: items, queryParam, selectedFilter });
      }
      onToggleContentGallery(-1);
    } else {
      const queryParam = {
        filter: filter,
        cdpFilter: selectedCDPTagsRef.current,
        tags: selectedTagsRef.current,
        searchTerm: search || "",
        pagination: {
          start: 0,
          rows: prelemId === "Prelem_053" ? 5 : 4,
        },
      };
      handleSelectedContent({ slots: items, queryParam, selectedFilter });
    }
  };

  const getSearchSuggestions = (fil, sear) => {
    fetchMultiSlotContentList({
      variables: {
        filter: fil,
        cdpFilter: selectedCDPTagsRef.current,
        tags: selectedTagsRef.current,
        searchTerm: sear,
        pagination: { start: 0, rows: rows },
        isSuggestive: true,
      },
    })
      .then((res) => {
        if (res?.data?.authoring_getDynamicContentSearch) {
          // add extra key value pair
          const pairAdded = customPairAdded(
            res?.data?.authoring_getDynamicContentSearch,
            multi_select,
            single_select,
          );
          // setSearchItems(res?.data?.authoring_getDynamicContentSearch);

          setSearchItems(pairAdded);
          setSuggestionEnabled(true);
        }
      })
      .catch(() => {})
      .finally(() => {
        setLoading(false);
      });
  };

  const getAllContentTypes = (fil, sear = "") => {
    setLoading(true);
    setStartIndex(0);
    fetchMultiSlotContentList({
      variables: {
        filter: fil,
        cdpFilter: selectedCDPTagsRef.current,
        tags: selectedTagsRef.current,
        searchTerm: sear,
        pagination: { start: 0, rows: rows },
        isSuggestive: false,
      },
    })
      .then((res) => {
        if (res?.data?.authoring_getDynamicContentSearch) {
          setLoading(false);
          // add extra key value pair
          const pairAdded = customPairAdded(
            res?.data?.authoring_getDynamicContentSearch,
            multi_select,
            single_select,
          );
          // setItems(res?.data?.authoring_getDynamicContentSearch);

          if (single_select || multi_select) {
            const newShallowArray = [...pairAdded];
            pairAdded?.forEach((val, index) => {
              const newObj: any = newShallowArray?.[index];
              selectedItems?.forEach((item: any, selectedIndex) => {
                newObj.is_checked =
                  val?.CurrentPageURL === item?.CurrentPageURL
                    ? item?.is_checked || true
                    : val?.is_checked;
                newObj.selectedIndex = selectedIndex;
                newShallowArray[index] = newObj;
              });
            });
            selectedItems?.length > 0 ? setItems(newShallowArray) : setItems(pairAdded);
          } else {
            setItems(pairAdded);
          }

          if (res?.data?.authoring_getDynamicContentSearch?.length < 18) {
            setIsLazyLoad(false);
          } else {
            setIsLazyLoad(true);
          }
        } else {
          setLoading(false);
        }
      })
      .catch(() => {})
      .finally(() => {
        setLoading(false);
      });
  };

  const fetchMoreContentTypes = (fil, sear, index) => {
    setLoading(true);
    fetchMultiSlotContentList({
      variables: {
        filter: fil,
        cdpFilter: selectedCDPTagsRef.current,
        tags: selectedTagsRef.current,
        searchTerm: sear || "",
        pagination: { start: index, rows: rows },
        isSuggestive: false,
      },
    })
      .then((res) => {
        if (res?.data?.authoring_getDynamicContentSearch) {
          // add extra key value pair
          const pairAdded = customPairAdded(
            res?.data?.authoring_getDynamicContentSearch,
            multi_select,
            single_select,
          );
          // const newData = [...items, ...(res?.data?.authoring_getDynamicContentSearch || {})];
          const newData = [...items, ...(pairAdded || {})];

          if (single_select || multi_select) {
            const newShallowArray = [...newData];
            // eslint-disable-next-line no-shadow
            newData?.forEach((val, index) => {
              const newObj: any = newShallowArray?.[index];
              selectedItems?.forEach((item: any) => {
                newObj.is_checked =
                  val?.CurrentPageURL === item?.CurrentPageURL
                    ? item?.is_checked || true
                    : val?.is_checked;
                newShallowArray[index] = newObj;
              });
            });
            selectedItems?.length > 0 ? setItems(newShallowArray) : setItems(() => newData);
          } else {
            setItems(() => newData);
          }

          if (res?.data?.authoring_getDynamicContentSearch?.length < 18) {
            setIsLazyLoad(false);
          } else {
            setIsLazyLoad(true);
          }
        }
        setLoading(false);
      })
      .catch((error: any) => {
        const statusCode = error?.networkError?.statusCode || 0;
        ShowToastError(t("api_error_toast"), statusCode);
        setLoading(false);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleTagOnChange = (event) => {
    try {
      const { id: categoryKey, value: tagValue, checked } = event.target;

      if (["Collection", "Content Type"].includes(categoryKey)) {
        if (checked) {
          const shallowTagObject = [...selectedCDPTags];
          shallowTagObject.push(tagValue);
          setSelectedCDPTags(shallowTagObject);
          selectedCDPTagsRef.current = shallowTagObject;
        } else {
          if (selectedCDPTagsRef.current.length === 1)
            ShowToastError("At least one Content/Collection must be selected", 0);
          else {
            const shallowTagObject = [...selectedCDPTags];
            shallowTagObject.splice(shallowTagObject.indexOf(tagValue), 1);
            setSelectedCDPTags(shallowTagObject);
            selectedCDPTagsRef.current = shallowTagObject;
          }
        }
      } else {
        if (checked) {
          const shallowTagObject = [...selectedTags];
          shallowTagObject.push(tagValue);
          setSelectedTags(shallowTagObject);
          selectedTagsRef.current = shallowTagObject;
        } else {
          const shallowTagObject = [...selectedTags];
          shallowTagObject.splice(shallowTagObject.indexOf(tagValue), 1);
          setSelectedTags(shallowTagObject);
          selectedTagsRef.current = shallowTagObject;
        }
      }

      // Refresh content with updated tags
      getAllContentTypes(filter, search);
    } catch (error) {
      ShowToastError(t("tag_update_error") || "Failed to update tags", 0);
    }
  };

  // const clearTag = () => {
  //   setselectedTag([]);
  //   tagRef.current = { ...tagRef?.current, tags: [] };
  //   setTagObj({});
  //   getTagData();
  //   getAllContentTypes(filter, search);
  // };
  const fetchMoreData = () => {
    const nextIndex = startIndex + rows;
    setStartIndex(() => nextIndex);
    fetchMoreContentTypes(filter, search, nextIndex);
  };

  const multiSelectedHandle = (_, index) => {
    const newShallowArray = [...items];
    const newObj: any = newShallowArray?.[index];
    newObj.is_checked = !newObj?.is_checked;
    newShallowArray[index] = newObj;
    if (newObj?.is_checked) {
      selectedItemsMultiSelect.current.push(newObj);
    } else {
      selectedItemsMultiSelect.current = selectedItemsMultiSelect.current.filter(
        (item) => item?.CurrentPageURL !== newObj?.CurrentPageURL,
      );
    }
    setItems(newShallowArray);
  };
  const singleSelectedHandle = (_, index) => {
    const newShallowArray = items.map((item, i) => {
      if (i !== index && item?.is_checked) {
        return { ...item, is_checked: false };
      }
      return item;
    });
    const newObj: any = newShallowArray?.[index];
    newObj.is_checked = !newObj?.is_checked;
    newShallowArray[index] = newObj;
    setItems(newShallowArray);
  };
  const handleSearchChange = useCallback(
    debounce((fil, sear) => getSearchSuggestions(fil, sear)),
    [],
  );

  useEffect(() => {
    if (Object.keys(tagData).length === 0) {
      getTagData();
    }
    const closeDropdown: Parameters<Document["addEventListener"]>[1] = (event: any) => {
      const hasClass = event?.target?.classList.contains("suggestionitems");
      if (!hasClass) {
        setSuggestionEnabled(false);
      }
    };
    document.addEventListener("click", closeDropdown);
    return () => {
      document.removeEventListener("click", closeDropdown);
    };
  }, []);

  // Enhanced useEffect for QueryParam type with proper tagObjRef.current building
  useEffect(() => {
    // This effect gets triggered on page load.
    if (tagData?.length > 0) {
      const initalTags = restrictToAvailableCDPTags
        ? getAvailableContentAndCollectionTags()
        : getInitialContentAndCollectionTags();
      setAllowedCDPTags(initalTags); // TODO: this should be selectedCDPTags from API (new property)
      // Check if selected_filter has tags, else use initalTags
      if (selectedCDPTags?.length === 0) {
        setSelectedCDPTags(initalTags);
        selectedCDPTagsRef.current = initalTags;
      }
      getAllContentTypes(filter, search);
    }
  }, [tagData]);

  const clearSelection = () => {
    const newShallowItems = [...items];
    newShallowItems.forEach((item) => {
      item.is_checked = false;
    });
    selectedItemsMultiSelect.current = [];
    setItems(newShallowItems);
  };

  const categoryClickHandle = () => {
    if (isShowItems) {
      setShowItems(false);
    } else {
      setShowItems(true);
    }
    if (isShowCategory) {
      setShowCategory(false);
    } else {
      setShowCategory(true);
    }
  };

  const backButtonHandle = () => {
    setShowCategory(false);
    setShowItems(true);
  };
  const onSearchChange = (e) => {
    const { value } = e.currentTarget;
    setSearch(value);
    if (value.length > 3) {
      handleSearchChange(filter, value);
    }
  };
  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      getAllContentTypes(filter, e.target.value);
      setSearch(e.target.value);
      setSuggestionEnabled(false);
    }
  };

  const resetSearch = () => {
    setSearch("");

    setSearchItems([]);
    setSuggestionEnabled(false);
    getAllContentTypes(filter, "");
  };

  // const handleFilterChange = (e) => {
  //   const { value } = e.target;
  //   setFilter(value);
  // };

  const handlePageSearch = (pageSearchName) => {
    setSearch(pageSearchName);
    getAllContentTypes(filter, pageSearchName);
    setSearchItems([]);
  };

  const getSelectionMessage = () => {
    switch (true) {
      case Boolean(single_select):
        return "You can select only one Card.";
      case Boolean(multi_select):
        return "You can select multiple Cards.";
      default:
        return "You cannot select Cards, but the prelem will display results based on query.";
    }
  };

  return (
    <Box
      sx={{
        position: "absolute",
        backgroundColor: "#fff",
        width: "100%",
        height: "100vh",
        zIndex: "10",
      }}>
      {loading && <Loader />}
      {isHideSearchBox && (
        <Grid
          container
          sx={{
            display: { xs: "flex", em: "none" },
            padding: "10px",
          }}>
          <Grid
            item
            xs={10}
            sm={10}
            sx={{
              margin: "auto 0",
              paddingBottom: { xs: "20px", sm: "20px" },
            }}>
            <FormControl fullWidth>
              <TextField
                sx={{
                  backgroundColor: "#f5f6f8",
                  height: "40px",
                  ".Platform-x-Input-root:before": {
                    borderBottom: "2px solid #2d2d39",
                  },
                  ".Platform-x-Input-root:after": {
                    borderBottom: "2px solid #000000",
                  },
                  ".Platform-x-Input-root.Mui-disabled:before": {
                    borderBottom: "2px solid #c3c3cb",
                  },
                  "Platform-x-OutlinedInput-root": {
                    borderTopRightRadius: "0px",
                    borderBottomRightRadius: "0px",
                  },
                }}
                variant='filled'
                placeholder={t("search")}
                value={search}
                onChange={onSearchChange}
                onKeyPress={handleKeyPress}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position='start'>
                      <ArrowBackIosIcon onClick={backToContentHandel} />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position='end'>
                      {search && (
                        <CloseRoundedIcon
                          onClick={resetSearch}
                          sx={{
                            cursor: "pointer",
                            position: "absolute",
                            right: "18px",
                          }}
                        />
                      )}
                    </InputAdornment>
                  ),
                }}
              />
            </FormControl>
          </Grid>
          <Grid
            item
            xs={2}
            sm={1}
            sx={{
              margin: "auto 0",
              paddingBottom: { xs: "20px", sm: "20px" },
              display: "flex",
            }}></Grid>
        </Grid>
      )}
      {!isHideSearchBox && (
        <Grid
          container
          sx={{
            display: { xs: "flex", em: "none" },
            padding: "15px",
          }}>
          <Grid
            item
            xs={9}
            sm={10}
            sx={{
              margin: "auto 0",
              paddingBottom: { xs: "20px", sm: "20px" },
              display: "flex",
            }}>
            <ArrowBackIosIcon onClick={() => onToggleContentGallery(-1)} />
            <Typography variant='h4bold'>{t("prelem_choose_content")}</Typography>
          </Grid>
          <Grid
            item
            xs={3}
            sm={1}
            sx={{
              margin: "auto 0",
              paddingBottom: { xs: "0px", sm: "20px" },
              display: "flex",
            }}>
            <Typography
              variant='h5regular'
              sx={{
                marginRight: "10px",
                marginLeft: "10px",
                display: "flex",
                alignItems: "center",
              }}
              onClick={mobileSearchIconHandel}>
              <SearchIcon />
            </Typography>
            <Typography
              variant='h5regular'
              sx={{ cursor: "pointer", display: "flex", alignItems: "center" }}
              onClick={categoryClickHandle}>
              {selectedCDPTags?.length > 0 ? (
                <Badge badgeContent={selectedCDPTags.length} color='error'>
                  <TagIcon />
                </Badge>
              ) : (
                <TagIcon />
              )}
            </Typography>
          </Grid>
        </Grid>
      )}
      <Grid
        container
        sx={{
          display: { xs: "none", em: "flex" },
          padding: "10px 15px",
          // height: "70px",
          alignItems: "center",
          justifyContent: "center",
          borderBottom: "1px solid #d9dbe9",
        }}>
        <Grid
          item
          xs={12}
          sm={12}
          em={3}
          lg={3}
          sx={{
            margin: "auto 0",
            display: "flex",
            alignItems: "center",
          }}>
          <BackArrow handleReturn={() => onToggleContentGallery(-1)} />
          <Typography variant='h4bold'>{t("prelem_choose_content")}</Typography>
        </Grid>

        <Grid
          item
          xs={12}
          sm={12}
          em={5}
          lg={6}
          sx={{
            padding: {
              xs: "10px 0",
              sm: "0",
              display: "flex",
              justifyContent: "center",
            },
          }}>
          <Grid item xs={12} sm={12} em={12} lg={9}>
            <FormControl fullWidth>
              <TextField
                className='contentTypeCard'
                variant='filled'
                placeholder={t("search")}
                value={search}
                onChange={onSearchChange}
                inputProps={{ style: { textAlign: "left" } }}
                onKeyPress={handleKeyPress}
                sx={{
                  ".Platform-x-InputBase-root.Platform-x-OutlinedInput-root": {
                    borderLeft: 0,
                    height: "50px",
                    border: "solid 1px #D9DBE9 !important",
                  },
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position='start' style={{ marginLeft: "15px" }}>
                      <img
                        src={searchIcon}
                        alt='search'
                        style={{ height: "20.25px", width: "20.25px" }}
                      />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position='end'>
                      {search && (
                        <CloseRoundedIcon
                          onClick={resetSearch}
                          sx={{
                            cursor: "pointer",
                            position: "absolute",
                            right: "18px",
                          }}
                        />
                      )}
                    </InputAdornment>
                  ),
                }}
              />
            </FormControl>
          </Grid>
        </Grid>

        <Grid
          item
          xs={12}
          sm={12}
          em={4}
          lg={3}
          container
          spacing={0}
          direction='column'
          alignItems='end'
          justifyContent='center'
          sx={{ display: "flex" }}>
          <Box justifyContent='end'>
            <Button
              variant='secondaryButton'
              className='sm'
              onClick={() => onToggleContentGallery(-1)}>
              {t("cancel")}
            </Button>
            <Button
              variant='primaryButton'
              className='sm'
              sx={{
                marginLeft: "12px",
              }}
              disabled={
                (multi_select || single_select) &&
                !allowEmptySelection &&
                !items.some((ele: any) => ele?.is_checked)
              }
              onClick={handleDone}>
              {t("save")}
            </Button>
          </Box>
        </Grid>
      </Grid>

      <Grid container spacing={0}>
        {isShowCategory && (
          <Grid
            container
            xs={12}
            // sm={3}
            em={3}
            xl={2}
            lg={2}
            sx={{ display: { xs: "block", em: "none" } }}>
            <Box
              sx={{
                padding: "15px",
                backgroundColor: "#ced3d9",
              }}>
              <Stack direction='row' alignItems='center' gap={1}>
                <Box component='span'>
                  <ArrowBackIosIcon onClick={backButtonHandle} />
                </Box>
                <Typography variant='h4medium' component='span' sx={{ textTransform: "uppercase" }}>
                  {t("categories")}
                </Typography>
                <Box
                  component='span'
                  sx={{
                    margin: "0px 15px 0px 0",
                    // float: 'right',
                    right: "0",
                    position: "absolute",
                  }}>
                  <SearchIcon />
                </Box>
              </Stack>
            </Box>
            {tagData &&
              tagData?.length > 0 &&
              tagData.map((categories, index) => {
                return (
                  <GalleryLeftSideBar
                    key={index}
                    categoriesdata={categories}
                    updateTagField={handleTagOnChange}
                    selectedCDPTags={selectedCDPTags}
                    selectedTags={selectedTags}
                    allowed_collection={allowed_collection}
                    allowed_content={allowed_content}
                    allowedCDPTags={allowedCDPTags}
                  />
                );
              })}
          </Grid>
        )}

        <Grid
          container
          xs={12}
          // sm={3}
          em={3}
          xl={2.5}
          lg={2.5}
          sx={{
            // backgroundColor: "#ced3d9",
            overflowY: "scroll",
            overflowX: "hidden",
            height: "calc(100vh - 100px)",
            // height: '620px',
            display: { xs: "none", em: "block" },
          }}>
          <Box
            sx={{
              padding: "15px",
              justifyContent: "space-between",
              display: "flex",
              backgroundColor: "#F7F7FC",
              // textTransform: "uppercase",
            }}>
            <Typography variant='h6semibold'>{capitalizeFirstLetter(t("categories"))}</Typography>

            {/* <Box
                component='span'
                sx={{
                  display: "flex",
                }}>
                <SearchIcon />
              </Box> */}
          </Box>
          {tagData &&
            tagData?.length > 0 &&
            (() => {
              const priority = ["Content Type", "Collection"];
              const orderedCategories = [
                ...tagData.filter((c) => c?.category === "Content Type"),
                ...tagData.filter((c) => c?.category === "Collection"),
                ...tagData.filter((c) => !priority.includes(c?.category)),
              ];

              return orderedCategories.map((categories, index) => {
                return (
                  <>
                    {index === 2 ? (
                      <Divider sx={{ margin: "10px 20px" }} key={"divider"}></Divider>
                    ) : null}
                    <GalleryLeftSideBar
                      key={index}
                      categoriesdata={categories}
                      updateTagField={handleTagOnChange}
                      selectedCDPTags={selectedCDPTags}
                      selectedTags={selectedTags}
                      allowed_collection={allowed_collection}
                      allowed_content={allowed_content}
                      allowedCDPTags={allowedCDPTags}
                    />
                  </>
                );
              });
            })()}
        </Grid>

        {isShowItems && (
          <Grid
            item
            xs={12}
            // sm={12}
            em={9}
            xl={9.5}
            lg={9.5}
            sx={{
              backgroundColor: theme?.palette?.customColor?.BGLIGHT,
              // border: "1px solid #ccc",
            }}>
            {/* <Box sx={{ padding: "10px 10px 0 10px" }}>
              <Typography variant='h6semibold'>
                Displaying results for Type
                {selectedCDPTags?.length > 0 ? " (" + selectedCDPTags?.join(", ") + ")" : "..."}
                {""}
                {selectedTags?.length > 0 ? " AND Supporting Tags" : ""}
                {""}
                {selectedTags?.length > 0 ? "(" + selectedTags?.join(", ") + ")" : ""}.
              </Typography>
            </Box> */}
            <Box
              sx={{
                padding: "14px 35px 10px 10px",
                display: "flex",
                alignItems: "flex-start",
                justifyContent: "center",
                flexDirection: "column",
              }}>
              <Typography
                variant='h6semibold'
                sx={{
                  display: {
                    xs: "none",
                    em: "block",
                    // em: 'block',
                    // lg: 'block',
                  },
                }}>
                {getSelectionMessage()}
              </Typography>
              <Typography
                variant='h6semibold'
                sx={{
                  display: {
                    xs: "none",
                    em: "block",
                    // em: 'block',
                    // lg: 'block',
                  },
                }}>
                SHOWING RESULTS {getQuery()}
              </Typography>
              {selectedItemsMultiSelect.current?.length > 0 ? (
                <Box sx={{ display: "flex", alignItems: "center", marginTop: "8px" }}>
                  <Typography variant='h6semibold'>
                    No. of Selected Items:{" "}
                    <span className={classes.activeColor}>
                      {selectedItemsMultiSelect.current.length}
                    </span>
                  </Typography>
                  <Box
                    component='span'
                    sx={{
                      float: "right",
                      display: "inline-block",
                      cursor: "pointer",
                      fontSize: "14px",
                      marginLeft: "10px",
                      color: theme?.palette?.customColor?.TERTIARY,
                      textDecoration: "underline",
                    }}
                    onClick={clearSelection}>
                    Clear
                  </Box>
                </Box>
              ) : null}

              {/* <Box
                  component='span'
                  sx={{
                    width: "34px",
                    height: "34px",
                    display: "flex",
                    alignItems: "center",
                    padding: "5px",
                    pointerEvents: "auto",
                    borderRadius: "3px",
                    border: "solid 1px #ced3d9",
                    backgroundColor: "#fff",
                  }}>
                  <img src={IconListViewSvg} alt='IconListViewSvg' />
                </Box> */}
            </Box>

            <Box
              sx={{
                paddingRight: "0px",
                paddingLeft: "0px",
                margin: "0px",
              }}>
              <GalleryItems
                loading={loading}
                galleryObj={items}
                isLazyLoad={isLazyLoad}
                multi_select={multi_select}
                fetchMoreData={fetchMoreData}
                multiSelectedHandle={multiSelectedHandle}
                singleSelectedHandle={singleSelectedHandle}
                single_select={single_select}
                selectedmultiSelect={selectedItemsMultiSelect.current}
              />
            </Box>
            {isSuggestionEnabled && searchItems?.length > 0 ? (
              <Box
                sx={{
                  position: {
                    xs: "absolute",
                    sm: "absolute",
                    em: "absolute",
                  },
                  top: "70px",
                  left: {
                    xs: "33px",
                    sm: "33px",
                    em: "220px",
                    lg: "415px",
                  },
                  width: { xs: "70%", sm: "70%", em: "46%", lg: "32%" },
                  zIndex: "999999",
                }}
                className='searchsuggestion'>
                <GalleryItemSearchResults
                  searchResults={searchItems}
                  handlePageSearch={handlePageSearch}
                />
              </Box>
            ) : null}
          </Grid>
        )}
      </Grid>
      <Box
        sx={{
          display: { xs: isShowCategory ? "none" : "block", em: "none" },
          position: "fixed",
          bottom: 0,
          right: 0,
          zIndex: "9999999",
        }}>
        <Box sx={{ margin: "0 25px 25px 0" }} onClick={handleDone}>
          <Fab size='large' color='primary' aria-label='add'>
            <DoneIcon style={{ color: "#fff" }} />
          </Fab>
        </Box>
      </Box>
    </Box>
  );
};

export default DynamicContentGallery;
